import * as React from 'react';
import { Query, Mutation } from 'react-apollo';
import GET_ADMIN_INTEGRATION_ENABLED_STATE from 'admin/queries/getAdminIntegrationEnabledState.gql';
import SET_ADMIN_INTEGRATIONS_MUTATION from 'admin/mutations/setAdminIntegrations.gql';
import ToastActions from 'actions/ToastActions';

// @ts-ignore
import AdminIntegration from 'admin/components/AdminIntegration';

import {
  upsertAdminToggledPartnerIntegration as upsertAdminToggledPartnerIntegrationType,
  upsertAdminToggledPartnerIntegrationVariables
} from 'admin/mutations/__generated__/upsertAdminToggledPartnerIntegration';
import { getAdminIntegrationEnabledState as AdminRemindPlanIntegrationContainerType } from 'admin/queries/__generated__/getAdminIntegrationEnabledState';
import { getAdminIntegrationEnabledState_adminToggledPartnerIntegrationState as AdminToggledPartnerIntegrationState } from 'admin/queries/__generated__/getAdminIntegrationEnabledState';

type ToggleIntegrationParamsType = {
  variables: {
    input: {
      isEnabled: boolean,
      orgUuid: string,
      providerKey: string,
    }
  }
};

type QueryVariablesType = {
  organizationUUID: string,
  providerKey: string,
};

type IntegrationType = {
  iconURL: string,
  isAdminToggleable: boolean,
  productLongDescription: string,
  productShortDescription: string,
  providerKey: string,
  title: string,
};

type Props = {
  integration: IntegrationType,
  organizationUUID: string,
  parentUUID: string,
};

function renderAdminIntegration(toggleIntegration: any, queryVariables: QueryVariablesType, integration: IntegrationType, isEnabled?: boolean) {
  const { organizationUUID, providerKey } = queryVariables;
  const toggleIntegrationParams = {
    variables: {
      input: {
        orgUuid: organizationUUID,
        isEnabled: !isEnabled,
        providerKey,
      }
    }
  };

  return (<AdminIntegration
    canEdit
    integration={integration}
    isEnabled={isEnabled}
    isToggleable
    organizationUUID={organizationUUID}
    toggleClickHandler={() => toggleIntegration ? toggleIntegration(toggleIntegrationParams) : undefined}
  />);
}

function renderMutation(isDistrict: boolean, queryVariables: QueryVariablesType, integration: IntegrationType, enabled?: boolean) {
  return (<Mutation<upsertAdminToggledPartnerIntegrationType, upsertAdminToggledPartnerIntegrationVariables>
    mutation={SET_ADMIN_INTEGRATIONS_MUTATION}
    onCompleted={() => ToastActions.showSuccessToast('Saved')}
    onError={error => ToastActions.showErrorToast(error.message)}
    refetchQueries={[{ query: GET_ADMIN_INTEGRATION_ENABLED_STATE, variables: queryVariables }]}>
    {(toggleIntegration: (params: ToggleIntegrationParamsType) => void) => {
      if (isDistrict) {
        return renderAdminIntegration(toggleIntegration, queryVariables, integration, enabled);
      } else {
        return (
          <Query<AdminRemindPlanIntegrationContainerType>
            query={GET_ADMIN_INTEGRATION_ENABLED_STATE}
            variables={queryVariables}
          >
            {({ loading, error, data }) => {
              if (loading || error || !data) {
                return null;
              } else {
                const isEnabledForSchool = (data.adminToggledPartnerIntegrationState as AdminToggledPartnerIntegrationState).isEnabled;
                return renderAdminIntegration(toggleIntegration, queryVariables, integration, Boolean(isEnabledForSchool));
              }
            }}
          </Query>
        )
      }
    }}
  </Mutation>);
}

export default function AdminRemindPlanIntegrationContainer(props: Props) {
  const { integration, organizationUUID, parentUUID } = props;
  const { providerKey } = integration;

  // query for enabled state of district (parent or self)
  const districtEnabledStateQueryVariables = {
    organizationUUID: parentUUID || organizationUUID,
    providerKey
  };

  return (<Query<AdminRemindPlanIntegrationContainerType>
    query={GET_ADMIN_INTEGRATION_ENABLED_STATE}
    variables={districtEnabledStateQueryVariables}>
    {({ loading, error, data }) => {
      if (loading || error || !data) {
        return null;
      } else {
        const isEnabledForDistrict = (data.adminToggledPartnerIntegrationState as AdminToggledPartnerIntegrationState).isEnabled;
        const currentOrgEnabledStateQueryVariables = { organizationUUID, providerKey };

        if (!parentUUID) { // is a district - can always edit
          return renderMutation(true, currentOrgEnabledStateQueryVariables, integration, Boolean(isEnabledForDistrict));
        } else if (isEnabledForDistrict) { // is a school - can't edit because is enabled for district
          return (<AdminIntegration
            canEdit={false}
            integration={integration}
            isEnabled
            isToggleable
            organizationUUID={organizationUUID}
          />);
        } else { // is a school - CAN edit because is not enabled for district
          return renderMutation(false, currentOrgEnabledStateQueryVariables, integration);
        }
      }
    }}
  </Query>);
}
